import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  CreditCard, Plus, Upload, Clock, CheckCircle2, XCircle, 
  Loader2, Eye, Image as ImageIcon, AlertCircle, UserPlus
} from 'lucide-react';
import { toast } from 'sonner';
import PaymentMethodsManager from '../components/PaymentMethodsManager';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Payments = () => {
  const { user, token, isOrganizer, canManagePayments, canVerifyPayments } = useAuth();
  const [payments, setPayments] = useState([]);
  const [myPayments, setMyPayments] = useState([]);
  const [events, setEvents] = useState([]);
  const [attendees, setAttendees] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [manualPaymentDialogOpen, setManualPaymentDialogOpen] = useState(false);
  const [viewProofDialog, setViewProofDialog] = useState(null);
  const [verifyDialog, setVerifyDialog] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [newPayment, setNewPayment] = useState({
    amount: '',
    payment_type: 'registration',
    payment_method: 'gcash',
    reference_number: '',
    notes: ''
  });
  const [manualPayment, setManualPayment] = useState({
    user_id: '',
    amount: '',
    payment_type: 'contribution',
    payment_method: 'cash',
    reference_number: '',
    notes: '',
    status: 'verified'
  });
  const [proofImage, setProofImage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const fileInputRef = useRef(null);

  // Can manage = organizer OR has Finance committee permission
  const canManage = (isOrganizer && isOrganizer()) || canManagePayments();
  const canVerify = (isOrganizer && isOrganizer()) || canVerifyPayments();

  const paymentTypes = [
    { value: 'registration', label: 'Registration Fee' },
    { value: 'contribution', label: 'Contribution' },
    { value: 'event_fee', label: 'Event Fee' },
  ];

  const paymentMethods = [
    { value: 'gcash', label: 'GCash' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'cash', label: 'Cash' },
  ];

  useEffect(() => {
    if (isOrganizer && isOrganizer()) {
      fetchEvents();
    } else {
      if (user?.event_id) {
        setSelectedEventId(user.event_id);
      }
    }
  }, [user, isOrganizer]);

  useEffect(() => {
    if (selectedEventId) {
      fetchPayments();
    }
  }, [selectedEventId, statusFilter]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/events/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data);
      if (response.data.length > 0) {
        setSelectedEventId(response.data[0].id);
      } else {
        setLoading(false);
      }
    } catch (error) {
      toast.error('Failed to load events');
      setLoading(false);
    }
  };

  const fetchAttendees = async () => {
    if (!selectedEventId) return;
    try {
      const response = await axios.get(`${API_URL}/api/event/attendees?event_id=${selectedEventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttendees(response.data);
    } catch (error) {
      console.error('Failed to load attendees');
    }
  };

  const fetchPayments = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const statusParam = statusFilter !== 'all' ? `&status=${statusFilter}` : '';
      const [allPaymentsRes, myPaymentsRes] = await Promise.all([
        canManage ? axios.get(`${API_URL}/api/event/payments?event_id=${selectedEventId}${statusParam}`, { headers }) : Promise.resolve({ data: [] }),
        axios.get(`${API_URL}/api/event/payments/my`, { headers })
      ]);
      setPayments(allPaymentsRes.data);
      setMyPayments(myPaymentsRes.data);
      
      // Also fetch attendees for manual payment
      if (canManage) {
        fetchAttendees();
      }
    } catch (error) {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const handleManualPaymentSubmit = async () => {
    if (!manualPayment.user_id || !manualPayment.amount) {
      toast.error('Please select an attendee and enter amount');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/event/payments/add?event_id=${selectedEventId}`,
        {
          ...manualPayment,
          amount: parseFloat(manualPayment.amount)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Payment recorded successfully!');
      setManualPaymentDialogOpen(false);
      setManualPayment({
        user_id: '',
        amount: '',
        payment_type: 'contribution',
        payment_method: 'cash',
        reference_number: '',
        notes: '',
        status: 'verified'
      });
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setProofImage(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault();
    if (!proofImage) {
      toast.error('Please upload proof of payment');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/event/payments`, {
        ...newPayment,
        amount: parseFloat(newPayment.amount),
        proof_image: proofImage
      });
      
      toast.success('Payment submitted for verification');
      setNewPayment({
        amount: '',
        payment_type: 'registration',
        payment_method: 'gcash',
        reference_number: '',
        notes: ''
      });
      setProofImage(null);
      setCreateDialogOpen(false);
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit payment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async (paymentId, status) => {
    try {
      await axios.put(`${API_URL}/api/event/payments/${paymentId}/verify`, {
        status,
        rejection_reason: status === 'rejected' ? rejectionReason : null
      });
      toast.success(`Payment ${status}`);
      setVerifyDialog(null);
      setRejectionReason('');
      fetchPayments();
    } catch (error) {
      toast.error('Failed to verify payment');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'verified':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="h-3 w-3 mr-1" /> Verified</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="h-3 w-3 mr-1" /> Rejected</Badge>;
      default:
        return <Badge className="bg-yellow-100 text-yellow-700"><Clock className="h-3 w-3 mr-1" /> Pending</Badge>;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-maroon" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="payments-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-maroon flex items-center gap-3">
              <CreditCard className="h-8 w-8" />
              Payment Tracking
            </h1>
            <p className="text-gray-600 mt-1">
              Track and verify member payments
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {canManage && events.length > 1 && (
              <Select value={selectedEventId} onValueChange={setSelectedEventId}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Select Event" />
                </SelectTrigger>
                <SelectContent>
                  {events.map(event => (
                    <SelectItem key={event.id} value={event.id}>
                      {event.event_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            {/* Manual Payment Button - Organizer Only */}
            {canManage && (
              <Button 
                variant="outline" 
                className="border-maroon text-maroon hover:bg-maroon hover:text-white"
                onClick={() => setManualPaymentDialogOpen(true)}
                data-testid="manual-payment-btn"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Record Payment
              </Button>
            )}
            
            {/* Submit Payment Button - Attendees Only (not organizers) */}
            {!canManage && (
              <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-maroon hover:bg-maroon-hover text-white rounded-full" data-testid="submit-payment-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Submit Payment
                  </Button>
                </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-heading text-maroon">Submit Payment</DialogTitle>
                  <DialogDescription>Upload your proof of payment for verification</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmitPayment} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Amount (₱)</Label>
                      <Input
                        type="number"
                        value={newPayment.amount}
                        onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        required
                        data-testid="payment-amount-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Payment Type</Label>
                      <Select 
                        value={newPayment.payment_type} 
                        onValueChange={(v) => setNewPayment({ ...newPayment, payment_type: v })}
                      >
                        <SelectTrigger data-testid="payment-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {paymentTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Payment Method</Label>
                      <Select 
                        value={newPayment.payment_method} 
                        onValueChange={(v) => setNewPayment({ ...newPayment, payment_method: v })}
                      >
                        <SelectTrigger data-testid="payment-method-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {paymentMethods.map((method) => (
                            <SelectItem key={method.value} value={method.value}>
                              {method.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Reference Number</Label>
                      <Input
                        value={newPayment.reference_number}
                        onChange={(e) => setNewPayment({ ...newPayment, reference_number: e.target.value })}
                        placeholder="Transaction ID"
                        data-testid="payment-reference-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Notes (Optional)</Label>
                    <Textarea
                      value={newPayment.notes}
                      onChange={(e) => setNewPayment({ ...newPayment, notes: e.target.value })}
                      placeholder="Any additional notes..."
                      data-testid="payment-notes-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Proof of Payment *</Label>
                    <div 
                      className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center cursor-pointer hover:border-maroon transition-colors"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {proofImage ? (
                        <div>
                          <img src={proofImage} alt="Proof" className="max-h-40 mx-auto mb-2 rounded-lg" />
                          <p className="text-sm text-green-600 font-medium">Proof uploaded</p>
                        </div>
                      ) : (
                        <div>
                          <ImageIcon className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                          <p className="text-sm text-gray-600">Click to upload GCash/Bank screenshot</p>
                          <p className="text-xs text-gray-400 mt-1">PNG, JPG up to 5MB</p>
                        </div>
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={handleFileChange}
                      className="hidden"
                      accept="image/*"
                      data-testid="proof-file-input"
                    />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      className="bg-maroon hover:bg-maroon-hover text-white" 
                      disabled={submitting}
                      data-testid="confirm-payment-btn"
                    >
                      {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                      {submitting ? 'Submitting...' : 'Submit Payment'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            )}
          </div>
        </div>

        {/* My Payments */}
        <Card className="border-0 shadow-sm bg-white" data-testid="my-payments-card">
          <CardHeader>
            <CardTitle className="font-heading text-xl">My Payments</CardTitle>
            <CardDescription>Your payment history</CardDescription>
          </CardHeader>
          <CardContent>
            {myPayments.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <CreditCard className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No payments submitted yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {myPayments.map((payment) => (
                  <div 
                    key={payment.id}
                    className="flex items-center justify-between p-4 bg-cream-paper rounded-xl"
                    data-testid={`my-payment-${payment.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-white rounded-lg">
                        <CreditCard className="h-5 w-5 text-maroon" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{formatCurrency(payment.amount)}</p>
                        <p className="text-sm text-gray-500">
                          {paymentTypes.find(t => t.value === payment.payment_type)?.label} • {paymentMethods.find(m => m.value === payment.payment_method)?.label}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(payment.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {getStatusBadge(payment.status)}
                      {payment.proof_image && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setViewProofDialog(payment)}
                          data-testid={`view-proof-${payment.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payment Methods Section (Organizer only) */}
        {canManage && selectedEventId && (
          <PaymentMethodsManager 
            eventId={selectedEventId} 
            token={token} 
            isOrganizer={true} 
            readOnly={false}
          />
        )}

        {/* All Payments (Organizer only) */}
        {canManage && (
          <Card className="border-0 shadow-sm bg-white" data-testid="all-payments-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-heading text-xl">All Payments</CardTitle>
                <CardDescription>Manage member payments</CardDescription>
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40" data-testid="status-filter">
                  <SelectValue placeholder="Filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="verified">Verified</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent>
              {payments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CreditCard className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No payments found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {payments.map((payment) => (
                    <div 
                      key={payment.id}
                      className={`flex items-center justify-between p-4 rounded-xl border ${
                        payment.status === 'pending' ? 'bg-yellow-50 border-yellow-200' : 'bg-gray-50 border-gray-200'
                      }`}
                      data-testid={`payment-${payment.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-white rounded-lg shadow-sm">
                          <CreditCard className="h-5 w-5 text-maroon" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{payment.user_name}</p>
                          <p className="text-lg font-bold text-maroon">{formatCurrency(payment.amount)}</p>
                          <p className="text-sm text-gray-500">
                            {paymentTypes.find(t => t.value === payment.payment_type)?.label} • {paymentMethods.find(m => m.value === payment.payment_method)?.label}
                          </p>
                          {payment.reference_number && (
                            <p className="text-xs text-gray-400">Ref: {payment.reference_number}</p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(payment.created_at).toLocaleDateString()}
                          </p>
                          {payment.status === 'rejected' && payment.rejection_reason && (
                            <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                              <AlertCircle className="h-3 w-3" /> {payment.rejection_reason}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(payment.status)}
                        {payment.proof_image && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setViewProofDialog(payment)}
                            data-testid={`admin-view-proof-${payment.id}`}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Proof
                          </Button>
                        )}
                        {payment.status === 'pending' && canVerify && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleVerify(payment.id, 'verified')}
                              className="bg-green-600 hover:bg-green-700 text-white"
                              data-testid={`verify-btn-${payment.id}`}
                            >
                              <CheckCircle2 className="h-4 w-4 mr-1" />
                              Verify
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setVerifyDialog(payment)}
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              data-testid={`reject-btn-${payment.id}`}
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              Reject
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* View Proof Dialog */}
        <Dialog open={!!viewProofDialog} onOpenChange={() => setViewProofDialog(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-maroon">Payment Proof</DialogTitle>
            </DialogHeader>
            {viewProofDialog?.proof_image && (
              <img 
                src={viewProofDialog.proof_image} 
                alt="Payment Proof" 
                className="w-full rounded-lg"
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={!!verifyDialog} onOpenChange={() => setVerifyDialog(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading text-maroon">Reject Payment</DialogTitle>
              <DialogDescription>Provide a reason for rejection</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Rejection Reason</Label>
                <Textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="e.g., Invalid reference number, amount mismatch..."
                  data-testid="rejection-reason-input"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setVerifyDialog(null)}>
                  Cancel
                </Button>
                <Button 
                  onClick={() => handleVerify(verifyDialog?.id, 'rejected')}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="confirm-reject-btn"
                >
                  Reject Payment
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Manual Payment Dialog */}
        <Dialog open={manualPaymentDialogOpen} onOpenChange={setManualPaymentDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-maroon">Record Manual Payment</DialogTitle>
              <DialogDescription>
                Record a payment for an attendee who paid in person or through other means
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Select Attendee *</Label>
                <Select 
                  value={manualPayment.user_id} 
                  onValueChange={(v) => setManualPayment({ ...manualPayment, user_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose attendee..." />
                  </SelectTrigger>
                  <SelectContent>
                    {attendees.map((attendee) => (
                      <SelectItem key={attendee.id} value={attendee.id}>
                        {attendee.first_name} {attendee.last_name} ({attendee.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Amount (₱) *</Label>
                  <Input
                    type="number"
                    value={manualPayment.amount}
                    onChange={(e) => setManualPayment({ ...manualPayment, amount: e.target.value })}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Payment Type</Label>
                  <Select 
                    value={manualPayment.payment_type} 
                    onValueChange={(v) => setManualPayment({ ...manualPayment, payment_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {paymentTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Payment Method</Label>
                  <Select 
                    value={manualPayment.payment_method} 
                    onValueChange={(v) => setManualPayment({ ...manualPayment, payment_method: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {paymentMethods.map((method) => (
                        <SelectItem key={method.value} value={method.value}>
                          {method.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select 
                    value={manualPayment.status} 
                    onValueChange={(v) => setManualPayment({ ...manualPayment, status: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="verified">Verified (Confirmed)</SelectItem>
                      <SelectItem value="pending">Pending (Needs Review)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Reference Number</Label>
                <Input
                  value={manualPayment.reference_number}
                  onChange={(e) => setManualPayment({ ...manualPayment, reference_number: e.target.value })}
                  placeholder="Optional - receipt or transaction number"
                />
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={manualPayment.notes}
                  onChange={(e) => setManualPayment({ ...manualPayment, notes: e.target.value })}
                  placeholder="Optional - any additional notes about this payment"
                  rows={2}
                />
              </div>

              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" onClick={() => setManualPaymentDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleManualPaymentSubmit}
                  className="bg-maroon hover:bg-maroon-hover text-white"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Recording...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Record Payment
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
  );
};

export default Payments;
